<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaFormField from '../base/GaiaFormField.vue';
import GaiaInput from '../base/inputs/GaiaInput.vue';
import GaiaModal from '../base/GaiaModal.vue';
import GaiaBanner from './GaiaBanner.vue';

/**
 * The "type its name to confirm" destructive dialog: a danger banner that says
 * what is about to happen, a field the user has to fill with the exact value,
 * and a commit button that stays disabled until they have. For the deletions
 * and severances a popconfirm is too light for — the row is gone for good, or
 * it takes other things with it.
 *
 * The parent owns the action: it flips `loading` while the request is out and
 * passes `error` back if it failed, so the dialog stays open with the message.
 */
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  /** What the user must type, character for character (outer whitespace aside). */
  confirmValue: {
    type: String,
    required: true,
  },
  /** The commit button's label — the verb + noun of the action ("Delete project"). */
  confirmLabel: {
    type: String,
    default: 'Delete',
  },
  bannerTitle: {
    type: String,
    default: 'This action cannot be undone',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** Why the last attempt failed; shown under the field, dialog stays open. */
  error: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ 'update:show': [value: boolean]; confirm: [] }>();

const typed = ref('');
const matches = computed(() => typed.value.trim() === props.confirmValue.trim());

// A reopened dialog must start empty — a pre-filled match would defeat the
// point of asking.
watch(
  () => props.show,
  open => {
    if (!open) typed.value = '';
  }
);

function confirm(): void {
  if (!matches.value || props.loading) return;
  emit('confirm');
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    :title="props.title"
    width="560px"
    :mask-closable="false"
    :closable="!props.loading"
    @update:show="value => emit('update:show', value)"
  >
    <n-flex vertical :size="14">
      <gaia-banner type="error" :title="props.bannerTitle">
        <slot />
      </gaia-banner>
      <gaia-form-field :label="`Type “${props.confirmValue}” to confirm`">
        <gaia-input
          v-model:value="typed"
          :placeholder="props.confirmValue"
          :disabled="props.loading"
          @keydown.enter="confirm"
        />
      </gaia-form-field>
      <gaia-banner v-if="props.error" plain type="error">{{ props.error }}</gaia-banner>
    </n-flex>
    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button :disabled="props.loading" @click="emit('update:show', false)"
          >Cancel</gaia-button
        >
        <!-- A plain error button, not GaiaDeleteButton: this is the dialog's
             commit action (see the note in GaiaDeleteButton). -->
        <gaia-button type="error" :disabled="!matches" :loading="props.loading" @click="confirm">
          {{ props.confirmLabel }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
